import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Save, TrendingUp, Trophy, TrendingDown, Target as TargetIcon, Smile, PartyPopper, RotateCcw, AlertTriangle, CalendarOff, Users, Calendar, Trash2, Edit, Plus } from 'lucide-react';
import SAR from '../components/SAR';
import { cheerMessage } from '../utils/gender';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Targets() {
  const { branch, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [employees, setEmployees] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchTargets, setBranchTargets] = useState({
    daily_target: '',
    weekly_target: '',
    monthly_target: '',
    quarterly_target: ''
  });
  // ── Bulk-save panel state ─────────────────────────────
  const [bulkMode, setBulkMode] = useState('same'); // 'same' | 'distribute'
  const [bulkSelected, setBulkSelected] = useState([]); // employee ids
  const [bulkValues, setBulkValues] = useState({ daily_target: '', weekly_target: '', monthly_target: '', quarterly_target: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  // Optional week-specific mode for bulk panel
  const _now = sunWeekNumberJS(new Date());
  const [bulkWeekEnabled, setBulkWeekEnabled] = useState(false);
  const [bulkYear, setBulkYear] = useState(_now.year);
  const [bulkWeek, setBulkWeek] = useState(_now.week);
  const [bulkHistory, setBulkHistory] = useState([]);
  const [bulkBranchWeekTarget, setBulkBranchWeekTarget] = useState(null); // branch's weekly target for selected (year, week)
  // When true (only in week-specific mode): unselected employees get target=0 for this week
  const [bulkZeroUnselected, setBulkZeroUnselected] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [employeesRes, branchRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/employees`),
        axios.get(`${API_URL}/api/branches`),
        axios.get(`${API_URL}/api/analytics/dashboard`),
      ]);
      setEmployees(employeesRes.data);
      setAnalytics(analyticsRes.data);
      if (branchRes.data && branchRes.data[0]) {
        const b = branchRes.data[0];
        setBranchTargets({
          daily_target: (b.daily_target ?? 0).toString(),
          weekly_target: (b.weekly_target ?? 0).toString(),
          monthly_target: (b.monthly_target ?? 0).toString(),
          quarterly_target: (b.quarterly_target ?? 0).toString(),
        });
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleBranchTargetSave = async () => {
    try {
      await axios.put(`${API_URL}/api/branches/${branch.id}`, {
        daily_target: parseFloat(branchTargets.daily_target) || 0,
        weekly_target: parseFloat(branchTargets.weekly_target) || 0,
        monthly_target: parseFloat(branchTargets.monthly_target) || 0,
        quarterly_target: parseFloat(branchTargets.quarterly_target) || 0,
      });
      toast.success('تم تحديث أهداف الفرع بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل تحديث أهداف الفرع');
    }
  };

  const handleEmployeeTargetChange = (employeeId, field, value) =>
    setEmployees(employees.map(e => e.id === employeeId ? { ...e, [field]: parseFloat(value) || 0 } : e));

  const handleEmployeeTargetSave = async (emp) => {
    try {
      await axios.put(`${API_URL}/api/employees/${emp.id}`, {
        daily_target: emp.daily_target || 0,
        weekly_target: emp.weekly_target || 0,
        monthly_target: emp.monthly_target || 0,
        quarterly_target: emp.quarterly_target || 0,
      });
      toast.success(`تم تحديث أهداف ${emp.name} بنجاح`);
      fetchData();
    } catch (error) {
      toast.error('فشل تحديث الأهداف');
    }
  };

  // ── Bulk save handlers ────────────────────────────────
  const toggleBulkEmployee = (id) =>
    setBulkSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleBulkAll = () =>
    setBulkSelected((prev) => (prev.length === employees.length ? [] : employees.map((e) => e.id)));

  const handleBulkSave = async () => {
    if (bulkSelected.length === 0) {
      toast.error('اختر موظفاً واحداً على الأقل');
      return;
    }
    const d = parseFloat(bulkValues.daily_target);
    const w = parseFloat(bulkValues.weekly_target);
    const m = parseFloat(bulkValues.monthly_target);
    const q = parseFloat(bulkValues.quarterly_target);
    const n = bulkSelected.length;

    // ── Week-specific historical save (weekly only) ───────────────────
    if (bulkWeekEnabled) {
      if (isNaN(w)) {
        toast.error('أدخل المستهدف الأسبوعي');
        return;
      }
      const wt = bulkMode === 'distribute' ? w / n : w;
      setBulkSaving(true);
      try {
        await axios.put(`${API_URL}/api/employees/bulk-targets`, {
          employee_ids: bulkSelected,
          weekly_target: wt,
          year: bulkYear,
          week: bulkWeek,
          zero_unselected: bulkZeroUnselected,
        });
        toast.success(
          bulkMode === 'distribute'
            ? `تم توزيع الأسبوعي على ${n} موظف لأسبوع ${bulkWeek} - ${bulkYear}${bulkZeroUnselected ? ' (البقية = 0)' : ''}`
            : `تم حفظ المستهدف الأسبوعي لـ ${n} موظف لأسبوع ${bulkWeek} - ${bulkYear}${bulkZeroUnselected ? ' (البقية = 0)' : ''}`
        );
        setBulkValues({ daily_target: '', weekly_target: '', monthly_target: '', quarterly_target: '' });
        setBulkSelected([]);
        loadBulkHistory();
        fetchData();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'فشل الحفظ');
      } finally { setBulkSaving(false); }
      return;
    }

    // ── Default mode (update employee defaults) ───────────────────────
    // A field is considered "provided" ONLY when the string is non-empty. This
    // way typing "0" is accepted as a real zero target (parseFloat("0") = 0
    // which passes !isNaN), while an untouched field is skipped so we don't
    // clobber existing values.
    const has = (v) => v !== '' && v !== null && v !== undefined && !isNaN(parseFloat(v));
    const hasAny = has(bulkValues.daily_target) || has(bulkValues.weekly_target) || has(bulkValues.monthly_target) || has(bulkValues.quarterly_target);
    if (!hasAny) {
      toast.error('أدخل قيمة واحدة على الأقل (اكتب 0 صراحةً للتصفير)');
      return;
    }
    // Distribute mode: split each provided amount equally across selected employees
    const payload = { employee_ids: bulkSelected };
    const compute = (val) => (bulkMode === 'distribute' ? val / n : val);
    if (has(bulkValues.daily_target)) payload.daily_target = compute(parseFloat(bulkValues.daily_target));
    if (has(bulkValues.weekly_target)) payload.weekly_target = compute(parseFloat(bulkValues.weekly_target));
    if (has(bulkValues.monthly_target)) payload.monthly_target = compute(parseFloat(bulkValues.monthly_target));
    if (has(bulkValues.quarterly_target)) payload.quarterly_target = compute(parseFloat(bulkValues.quarterly_target));

    setBulkSaving(true);
    try {
      await axios.put(`${API_URL}/api/employees/bulk-targets`, payload);
      toast.success(
        bulkMode === 'distribute'
          ? `تم التوزيع على ${n} موظف`
          : `تم حفظ المستهدف لـ ${n} موظف`
      );
      setBulkValues({ daily_target: '', weekly_target: '', monthly_target: '', quarterly_target: '' });
      setBulkSelected([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل الحفظ الجماعي');
    } finally {
      setBulkSaving(false);
    }
  };

  const loadBulkHistory = async () => {
    if (!bulkWeekEnabled) { setBulkHistory([]); return; }
    try {
      const r = await axios.get(`${API_URL}/api/employees/weekly-targets/${bulkYear}/${bulkWeek}`);
      setBulkHistory(r.data.items || []);
    } catch (e) { /* ignore */ }
  };

  // Fetch branch's saved weekly target for the selected (year, week). Falls back to
  // the branch's default weekly_target if no specific week snapshot exists.
  const loadBranchWeekTarget = async () => {
    if (!bulkWeekEnabled || !branch?.id) { setBulkBranchWeekTarget(null); return; }
    try {
      const r = await axios.get(`${API_URL}/api/branches/${branch.id}/weekly-targets`);
      const row = (r.data.items || []).find(it => it.year === bulkYear && it.week === bulkWeek);
      if (row && row.weekly_target != null) {
        setBulkBranchWeekTarget(Number(row.weekly_target));
      } else {
        // fallback to branch default
        const wt = parseFloat(branchTargets.weekly_target);
        setBulkBranchWeekTarget(Number.isFinite(wt) ? wt : null);
      }
    } catch (e) {
      setBulkBranchWeekTarget(null);
    }
  };

  useEffect(() => { loadBulkHistory(); }, [bulkWeekEnabled, bulkYear, bulkWeek]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadBranchWeekTarget(); }, [bulkWeekEnabled, bulkYear, bulkWeek, branch?.id, branchTargets.weekly_target]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-distribute: when week-mode is ON, the branch target is known, and at least
  // one employee is selected → auto-fill weekly_target with branch total in distribute mode.
  useEffect(() => {
    if (!bulkWeekEnabled) return;
    if (!bulkBranchWeekTarget || bulkSelected.length === 0) return;
    setBulkMode('distribute');
    setBulkValues((v) => ({ ...v, weekly_target: String(bulkBranchWeekTarget) }));
  }, [bulkWeekEnabled, bulkBranchWeekTarget, bulkSelected.length]);

  const deleteBulkHistoryRow = async (employee_id) => {
    if (!window.confirm('حذف هذا المستهدف الأسبوعي للموظف؟')) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${employee_id}/weekly-targets/${bulkYear}/${bulkWeek}`);
      toast.success('تم الحذف');
      loadBulkHistory();
      fetchData();
    } catch (e) { toast.error('فشل الحذف'); }
  };

  if (loading) return <div className="text-center py-12">جاري التحميل...</div>;

  const topEmployees = analytics?.top_employees || [];
  const bottomEmployees = analytics?.bottom_employees || [];
  const employeeSales = analytics?.employee_sales || [];

  const BranchCard = ({ title, target, achieved, color = 'brand_blue' }) => {
    const remaining = Math.max((target || 0) - (achieved || 0), 0);
    const pct = target > 0 ? (achieved / target) * 100 : 0;
    return (
      <Card className="p-5 border-border">
        <p className="text-xs font-bold text-muted-foreground uppercase">{title}</p>
        <div className="mt-3 space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">الهدف</span>
            <span className="text-sm font-bold font-numbers">{(target || 0).toLocaleString('en-US')} <SAR /></span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">المحقق</span>
            <span className="text-sm font-bold text-success font-numbers">{(achieved || 0).toLocaleString('en-US')} <SAR /></span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">المتبقي</span>
            <span className={`text-sm font-bold font-numbers ${remaining === 0 ? 'text-success' : 'text-warning'}`}>
              {remaining.toLocaleString('en-US')} <SAR />
            </span>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <div className="flex-1 bg-secondary rounded-full h-2">
              <div className={`bg-${color} h-2 rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            <span className="text-xs font-bold font-numbers">{pct.toFixed(0)}%</span>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-8" data-testid="targets-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black text-primary">المستهدفات والأداء</h1>
        <p className="text-muted-foreground mt-1">
          {isAdmin ? 'تحكم بأهداف الفرع والموظفين' : 'استعراض جميع المستهدفات والإنجاز'}
        </p>
      </div>

      {/* Branch target cards */}
      <div>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-brand_blue" />
          إنجاز الفرع
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <BranchCard title="يومي" target={analytics?.branch_daily_target} achieved={analytics?.daily_sales} color="warning" />
          <BranchCard title="أسبوعي" target={analytics?.branch_weekly_target} achieved={analytics?.weekly_sales} color="success" />
          <BranchCard title="شهري" target={analytics?.branch_monthly_target} achieved={analytics?.monthly_sales} color="brand_blue" />
          <BranchCard title="ربع سنوي" target={analytics?.branch_quarterly_target} achieved={analytics?.quarterly_sales} color="primary" />
        </div>
      </div>

      {/* Weekly per-employee indicator — BIG emphasized cards */}
      <div>
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <TargetIcon className="h-5 w-5 text-brand_blue" />
          مؤشر المستهدف الأسبوعي لكل موظف
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="weekly-employee-indicators">
          {(analytics?.employee_sales || []).length === 0 ? (
            <p className="text-muted-foreground">لا توجد بيانات موظفين</p>
          ) : (analytics.employee_sales).map((emp) => {
            const target = emp.weekly_target || 0;
            const achieved = emp.weekly_total || 0;
            const remaining = Math.max(target - achieved, 0);
            const over = achieved > target && target > 0;
            const exact = target > 0 && achieved === target;
            const reached = target > 0 && achieved >= target;
            const pct = target > 0 ? (achieved / target) * 100 : 0;
            
            let stateBadge = null;
            if (over) {
              stateBadge = (
                <div
                  data-testid={`weekly-state-${emp.id}`}
                  data-state="over"
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-l from-success/20 to-success/10 border-2 border-success text-success"
                >
                  <PartyPopper className="h-6 w-6 animate-pulse" />
                  <span className="text-xl md:text-2xl font-black">{cheerMessage(emp.name, true)}</span>
                  <PartyPopper className="h-6 w-6 animate-pulse" />
                </div>
              );
            } else if (exact || reached) {
              stateBadge = (
                <div
                  data-testid={`weekly-state-${emp.id}`}
                  data-state="done"
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-success/10 border-2 border-success text-success"
                >
                  <span className="text-3xl md:text-4xl font-black">تم</span>
                  <Smile className="h-8 w-8 md:h-10 md:w-10" strokeWidth={2.5} />
                </div>
              );
            }
            
            return (
              <Card
                key={emp.id}
                data-testid={`weekly-emp-${emp.id}`}
                className={`p-4 md:p-6 border-2 transition-all ${
                  over ? 'border-success bg-success/5'
                    : reached ? 'border-success/60 bg-success/5'
                    : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-black text-primary">{emp.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">المستهدف الأسبوعي</p>
                    {emp.attendance === 'on_leave' && (
                      <div className="mt-2 flex flex-col gap-0.5" data-testid={`weekly-attendance-${emp.id}`}>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 font-bold text-xs w-fit">
                          <CalendarOff className="h-3 w-3" />
                          إجازة
                        </span>
                        {emp.leave_period && (
                          <span className="text-[10px] text-muted-foreground font-numbers" dir="ltr">
                            {emp.leave_period.start} → {emp.leave_period.end}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <span className={`text-2xl font-black font-numbers ${reached ? 'text-success' : 'text-primary'}`}>
                      {pct.toFixed(0)}%
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
                  <div className="text-center p-2 md:p-3 rounded-lg bg-primary/5 min-w-0">
                    <p className="text-[11px] md:text-xs font-bold text-muted-foreground mb-1">الهدف</p>
                    <p className="text-base md:text-2xl font-black text-primary font-numbers flex flex-wrap items-center justify-center gap-1 leading-tight break-all">
                      {target.toLocaleString('en-US')}
                      <SAR size="0.85em" />
                    </p>
                  </div>
                  <div className="text-center p-2 md:p-3 rounded-lg bg-success/10 min-w-0">
                    <p className="text-[11px] md:text-xs font-bold text-muted-foreground mb-1">المحقق</p>
                    <p className="text-base md:text-2xl font-black text-success font-numbers flex flex-wrap items-center justify-center gap-1 leading-tight break-all">
                      {achieved.toLocaleString('en-US')}
                      <SAR size="0.85em" />
                    </p>
                  </div>
                  <div className={`text-center p-2 md:p-3 rounded-lg min-w-0 ${remaining > 0 ? 'bg-destructive/10' : 'bg-success/10'}`}>
                    <p className="text-[11px] md:text-xs font-bold text-muted-foreground mb-1">المتبقي</p>
                    <p
                      data-testid={`weekly-remaining-${emp.id}`}
                      className={`text-lg md:text-3xl font-black font-numbers flex flex-wrap items-center justify-center gap-1 leading-tight break-all ${
                        remaining > 0 ? 'text-destructive' : 'text-success'
                      }`}
                    >
                      {remaining.toLocaleString('en-US')}
                      <SAR size="0.9em" />
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1 bg-secondary rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${reached ? 'bg-success' : 'bg-brand_blue'}`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                </div>

                {stateBadge}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Top / Bottom performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-2 border-success/30 bg-success/5" data-testid="top-employees-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-lg bg-success/20 flex items-center justify-center">
              <Trophy className="h-6 w-6 text-success" />
            </div>
            <div>
              <h3 className="text-lg font-black">أفضل موظفي المبيعات</h3>
              <p className="text-xs text-muted-foreground">حسب إجمالي المبيعات الشهرية</p>
            </div>
          </div>
          <div className="space-y-2">
            {topEmployees.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">لا توجد بيانات</p>
            ) : topEmployees.map((e, i) => {
              const medal = ['🥇', '🥈', '🥉'][i];
              return (
                <div key={e.id} className="flex items-center justify-between p-3 rounded-lg bg-white/60" data-testid={`top-employee-${i + 1}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-success/15 flex items-center justify-center text-2xl" title={`المركز ${i + 1}`}>
                      {medal || <span className="text-base font-black text-success font-numbers">{i + 1}</span>}
                    </div>
                    <span className="font-bold">{e.name}</span>
                  </div>
                  <span className="font-black text-success font-numbers">{e.monthly_total.toLocaleString('en-US')} <SAR /></span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-6 border-2 border-destructive/30 bg-destructive/5" data-testid="bottom-employees-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-lg bg-destructive/20 flex items-center justify-center">
              <TrendingDown className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <h3 className="text-lg font-black">أقل موظفي المبيعات</h3>
              <p className="text-xs text-muted-foreground">حسب إجمالي المبيعات الشهرية</p>
            </div>
          </div>
          <div className="space-y-2">
            {bottomEmployees.length === 0 ? (
              <p className="text-center text-muted-foreground py-6">لا توجد بيانات كافية</p>
            ) : bottomEmployees.map((e, i) => (
              <div key={e.id} className="flex items-center justify-between p-3 rounded-lg bg-white/60">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-destructive text-white flex items-center justify-center font-black">
                    {i + 1}
                  </div>
                  <span className="font-bold">{e.name}</span>
                </div>
                <span className="font-black text-destructive font-numbers">{e.monthly_total.toLocaleString('en-US')} <SAR /></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Per-employee indicators */}
      <Card className="p-6 border-border">
        <h3 className="text-xl font-bold mb-4">مؤشرات الموظفين (الهدف / المحقق / المتبقي)</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-accent/50">
              <tr>
                <th className="text-right py-3 px-4 text-xs font-bold">الموظف</th>
                <th className="text-right py-3 px-4 text-xs font-bold">الفترة</th>
                <th className="text-right py-3 px-4 text-xs font-bold">الهدف</th>
                <th className="text-right py-3 px-4 text-xs font-bold">المحقق</th>
                <th className="text-right py-3 px-4 text-xs font-bold">المتبقي</th>
                <th className="text-right py-3 px-4 text-xs font-bold">النسبة</th>
              </tr>
            </thead>
            <tbody>
              {employeeSales.flatMap((e) => {
                const rows = [
                  { key: `${e.id}-d`, label: 'يومي', target: e.daily_target, achieved: e.daily_total, pct: e.daily_percentage },
                  { key: `${e.id}-w`, label: 'أسبوعي', target: e.weekly_target, achieved: e.weekly_total, pct: e.weekly_percentage },
                  { key: `${e.id}-m`, label: 'شهري', target: e.monthly_target, achieved: e.monthly_total, pct: e.monthly_percentage },
                ];
                return rows.map((r, idx) => (
                  <tr key={r.key} className="border-t border-border">
                    {idx === 0 && <td rowSpan="3" className="py-3 px-4 font-bold align-top">{e.name}</td>}
                    <td className="py-2 px-4 text-sm text-muted-foreground">{r.label}</td>
                    <td className="py-2 px-4 text-sm font-numbers">{(r.target || 0).toLocaleString('en-US')}</td>
                    <td className="py-2 px-4 text-sm text-success font-bold font-numbers">{(r.achieved || 0).toLocaleString('en-US')}</td>
                    <td className="py-2 px-4 text-sm text-warning font-numbers">{Math.max((r.target || 0) - (r.achieved || 0), 0).toLocaleString('en-US')}</td>
                    <td className="py-2 px-4 text-sm">
                      <div className="flex items-center gap-2 max-w-[140px]">
                        <div className="flex-1 bg-secondary rounded-full h-2">
                          <div className={`h-2 rounded-full ${(r.pct || 0) >= 100 ? 'bg-success' : 'bg-brand_blue'}`} style={{ width: `${Math.min(r.pct || 0, 100)}%` }} />
                        </div>
                        <span className="text-xs font-bold font-numbers">{(r.pct || 0).toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Admin: reset achievements */}
      {isAdmin && <ResetAchievementsCard employees={employees} onReset={fetchData} />}

      {/* Admin-only: edit branch + employee targets */}
      {isAdmin && (
        <Tabs defaultValue="branch" className="w-full">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="branch">تعديل أهداف الفرع</TabsTrigger>
            <TabsTrigger value="employees">تعديل أهداف الموظفين</TabsTrigger>
          </TabsList>

          <TabsContent value="branch" className="space-y-6 mt-6">
            <Card className="p-6 border-border" data-testid="branch-targets-card">
              <h3 className="text-lg font-bold mb-4">أهداف الفرع</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>الهدف اليومي <SAR /></Label>
                  <Input data-testid="branch-daily-target-input" type="number" step="0.01"
                    value={branchTargets.daily_target}
                    onChange={(e) => setBranchTargets({ ...branchTargets, daily_target: e.target.value })}
                    className="text-right h-11 font-numbers" />
                </div>
                <div className="space-y-2">
                  <Label>الهدف الأسبوعي <SAR /></Label>
                  <Input data-testid="branch-weekly-target-input" type="number" step="0.01"
                    value={branchTargets.weekly_target}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Auto-derive daily target = weekly / 7 (rounded to 2 decimals)
                      const wt = Number(v);
                      const auto_daily = Number.isFinite(wt) && wt >= 0 ? +(wt / 7).toFixed(2) : '';
                      setBranchTargets({ ...branchTargets, weekly_target: v, daily_target: auto_daily === '' ? branchTargets.daily_target : String(auto_daily) });
                    }}
                    className="text-right h-11 font-numbers" />
                  <p className="text-[10px] text-foreground/55">يُحسب الهدف اليومي تلقائياً = الأسبوعي ÷ 7</p>
                  <WeeklyTargetWeekPicker
                    branchId={branch?.id}
                    currentValue={branchTargets.weekly_target}
                    onApplyValue={(v) => {
                      const wt = Number(v);
                      const auto_daily = Number.isFinite(wt) && wt >= 0 ? +(wt / 7).toFixed(2) : '';
                      setBranchTargets({
                        ...branchTargets,
                        weekly_target: String(v),
                        daily_target: auto_daily === '' ? branchTargets.daily_target : String(auto_daily),
                      });
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>الهدف الشهري <SAR /></Label>
                  <Input data-testid="branch-monthly-target-input" type="number" step="0.01"
                    value={branchTargets.monthly_target}
                    onChange={(e) => setBranchTargets({ ...branchTargets, monthly_target: e.target.value })}
                    className="text-right h-11 font-numbers" />
                </div>
                <div className="space-y-2">
                  <Label>الهدف الربع سنوي <SAR /></Label>
                  <Input data-testid="branch-quarterly-target-input" type="number" step="0.01"
                    value={branchTargets.quarterly_target}
                    onChange={(e) => setBranchTargets({ ...branchTargets, quarterly_target: e.target.value })}
                    className="text-right h-11 font-numbers" />
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <Button onClick={handleBranchTargetSave} data-testid="save-branch-targets-button" className="gap-2">
                  <Save className="h-4 w-4" /> حفظ أهداف الفرع
                </Button>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="employees" className="space-y-4 mt-6">
            {/* ── لوحة الحفظ الجماعي ─────────────────────────── */}
            {employees.length > 0 && (
              <Card className="p-5 border-2 border-brand_blue/30 bg-brand_blue/5" data-testid="bulk-targets-panel">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 text-brand_blue" />
                  <h3 className="text-lg font-black text-brand_blue">حفظ جماعي للمستهدفات</h3>
                </div>

                {/* Mode toggle */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    type="button"
                    size="sm"
                    variant={bulkMode === 'same' ? 'default' : 'outline'}
                    onClick={() => setBulkMode('same')}
                    data-testid="bulk-mode-same"
                  >
                    نفس المستهدف لكل موظف
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={bulkMode === 'distribute' ? 'default' : 'outline'}
                    onClick={() => setBulkMode('distribute')}
                    data-testid="bulk-mode-distribute"
                  >
                    توزيع المستهدف بالتساوي
                  </Button>
                </div>

                {/* Optional: week-specific historical save */}
                <div className="mb-4 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/60 p-3" data-testid="bulk-week-toggle-block">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bulkWeekEnabled}
                      onChange={(e) => setBulkWeekEnabled(e.target.checked)}
                      className="accent-amber-600 h-4 w-4"
                      data-testid="bulk-week-toggle"
                    />
                    <span className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                      <Calendar className="h-4 w-4" />
                      حفظ المستهدف الأسبوعي لأسبوع محدد (ماضٍ / حالي / مستقبلي)
                    </span>
                  </label>
                  {bulkWeekEnabled && (
                    <div className="mt-3 space-y-2">
                      <div className="grid grid-cols-2 gap-2 max-w-md">
                        <div>
                          <Label className="text-xs">السنة</Label>
                          <Select value={String(bulkYear)} onValueChange={(v) => setBulkYear(Number(v))}>
                            <SelectTrigger className="h-9 text-sm" data-testid="bulk-week-year"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 6 }, (_, i) => _now.year - 3 + i).map((y) => (
                                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">رقم الأسبوع</Label>
                          <Select value={String(bulkWeek)} onValueChange={(v) => setBulkWeek(Number(v))}>
                            <SelectTrigger className="h-9 text-sm" data-testid="bulk-week-week"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-64">
                              {Array.from({ length: 53 }, (_, i) => i + 1).map((w) => (
                                <SelectItem key={w} value={String(w)}>
                                  أسبوع {w}{w === _now.week && bulkYear === _now.year ? ' (الحالي)' : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-[11px] text-amber-800 font-numbers" dir="ltr">
                        {(() => { const r = sunWeekRangeJS(bulkYear, bulkWeek); return `${r.start} → ${r.end}`; })()}
                      </p>
                      {bulkBranchWeekTarget != null && (
                        <div className="rounded-md bg-amber-100/70 border border-amber-300 px-2.5 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" data-testid="bulk-branch-week-target">
                          <span className="font-bold text-amber-900">🏢 مستهدف الفرع لهذا الأسبوع:</span>
                          <span className="font-numbers font-black text-emerald-700">
                            {Number(bulkBranchWeekTarget).toLocaleString('en-US')} <SAR size="0.85em" />
                          </span>
                          {bulkSelected.length > 0 && (
                            <>
                              <span className="text-amber-700">•</span>
                              <span className="font-bold text-amber-900">لكل موظف ({bulkSelected.length}):</span>
                              <span className="font-numbers font-black text-blue-700">
                                {(bulkBranchWeekTarget / bulkSelected.length).toLocaleString('en-US', { maximumFractionDigits: 2 })} <SAR size="0.85em" />
                              </span>
                              <span className="text-amber-700">·</span>
                              <span className="text-amber-900">يومي ÷ 7:</span>
                              <span className="font-numbers font-bold text-purple-700">
                                {(bulkBranchWeekTarget / bulkSelected.length / 7).toLocaleString('en-US', { maximumFractionDigits: 2 })} <SAR size="0.85em" />
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      <p className="text-[11px] text-amber-700">
                        ⚠️ سيتم حفظ المستهدف الأسبوعي فقط ولن يتم تغيير المستهدف الافتراضي للموظف. يتم عرض الهدف اليومي تلقائياً (= الأسبوعي ÷ 7) — الحقول الشهري/الربع سنوي معطّلة في هذا الوضع.
                      </p>
                      <label className="flex items-center gap-2 cursor-pointer mt-2 pt-2 border-t border-amber-200" data-testid="bulk-zero-unselected-block">
                        <input
                          type="checkbox"
                          checked={bulkZeroUnselected}
                          onChange={(e) => setBulkZeroUnselected(e.target.checked)}
                          className="accent-amber-700 h-4 w-4"
                          data-testid="bulk-zero-unselected"
                        />
                        <span className="text-[11px] font-bold text-amber-800">
                          اجعل مستهدف الموظفين غير المحددين = 0 لهذا الأسبوع
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                {/* Employee multi-select */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-bold">اختر الموظفين</Label>
                    <button
                      type="button"
                      onClick={toggleBulkAll}
                      className="text-xs font-bold text-brand_blue hover:underline"
                      data-testid="bulk-toggle-all"
                    >
                      {bulkSelected.length === employees.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-md bg-white border border-border max-h-48 overflow-y-auto">
                    {employees.map((emp) => {
                      const checked = bulkSelected.includes(emp.id);
                      return (
                        <label
                          key={emp.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm transition-colors ${
                            checked ? 'bg-brand_blue/10 text-brand_blue font-bold' : 'hover:bg-accent'
                          }`}
                          data-testid={`bulk-emp-checkbox-${emp.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleBulkEmployee(emp.id)}
                            className="accent-brand_blue"
                          />
                          <span className="truncate">{emp.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    عدد المحددين: <span className="font-bold font-numbers">{bulkSelected.length}</span> من {employees.length}
                  </p>
                </div>

                {/* Target inputs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs font-bold">
                      {bulkMode === 'distribute' ? 'إجمالي يومي' : 'يومي'} <SAR />
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bulkValues.daily_target}
                      onChange={(e) => setBulkValues({ ...bulkValues, daily_target: e.target.value })}
                      data-testid="bulk-daily-input"
                      className="text-right h-10 font-numbers"
                      placeholder="غير محدد"
                      disabled={bulkWeekEnabled}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold">
                      {bulkMode === 'distribute' ? 'إجمالي أسبوعي' : 'أسبوعي'} <SAR />
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bulkValues.weekly_target}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Weekly → auto-fill daily = weekly ÷ 7 (in both regular and
                        // week-specific modes). Manager can still override the daily field.
                        const wt = Number(v);
                        const auto_daily = Number.isFinite(wt) && wt >= 0 ? +(wt / 7).toFixed(2) : '';
                        setBulkValues({
                          ...bulkValues,
                          weekly_target: v,
                          daily_target: auto_daily === '' ? bulkValues.daily_target : String(auto_daily),
                        });
                      }}
                      data-testid="bulk-weekly-input"
                      className="text-right h-10 font-numbers"
                      placeholder="غير محدد"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold">
                      {bulkMode === 'distribute' ? 'إجمالي شهري' : 'شهري'} <SAR />
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bulkValues.monthly_target}
                      onChange={(e) => setBulkValues({ ...bulkValues, monthly_target: e.target.value })}
                      data-testid="bulk-monthly-input"
                      className="text-right h-10 font-numbers"
                      placeholder="غير محدد"
                      disabled={bulkWeekEnabled}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold">
                      {bulkMode === 'distribute' ? 'إجمالي ربع سنوي' : 'ربع سنوي'} <SAR />
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bulkValues.quarterly_target}
                      onChange={(e) => setBulkValues({ ...bulkValues, quarterly_target: e.target.value })}
                      data-testid="bulk-quarterly-input"
                      className="text-right h-10 font-numbers"
                      placeholder="غير محدد"
                      disabled={bulkWeekEnabled}
                    />
                  </div>
                </div>

                {/* Distribute preview */}
                {bulkMode === 'distribute' && bulkSelected.length > 0 && (
                  <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="bulk-distribute-preview">
                    <div className="font-bold mb-1">معاينة التوزيع على {bulkSelected.length} موظف:</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1 font-numbers">
                      {bulkValues.daily_target !== '' && !isNaN(parseFloat(bulkValues.daily_target)) && (
                        <div>يومي/موظف: {(parseFloat(bulkValues.daily_target) / bulkSelected.length).toFixed(2)}</div>
                      )}
                      {bulkValues.weekly_target !== '' && !isNaN(parseFloat(bulkValues.weekly_target)) && (
                        <div>أسبوعي/موظف: {(parseFloat(bulkValues.weekly_target) / bulkSelected.length).toFixed(2)}</div>
                      )}
                      {bulkValues.monthly_target !== '' && !isNaN(parseFloat(bulkValues.monthly_target)) && (
                        <div>شهري/موظف: {(parseFloat(bulkValues.monthly_target) / bulkSelected.length).toFixed(2)}</div>
                      )}
                      {bulkValues.quarterly_target && (
                        <div>ربع سنوي/موظف: {(parseFloat(bulkValues.quarterly_target) / bulkSelected.length).toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleBulkSave}
                    disabled={bulkSaving || bulkSelected.length === 0}
                    data-testid="bulk-save-button"
                    className="gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {bulkSaving ? 'جاري الحفظ...' : (bulkWeekEnabled
                      ? `حفظ أسبوع ${bulkWeek}/${bulkYear} لـ ${bulkSelected.length}`
                      : `حفظ للجميع (${bulkSelected.length})`)}
                  </Button>
                </div>

                {/* Historical-week saved rows */}
                {bulkWeekEnabled && bulkHistory.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-amber-200/60" data-testid="bulk-week-history">
                    <p className="text-xs font-bold text-amber-800 mb-2">
                      المستهدفات المحفوظة لأسبوع {bulkWeek} - {bulkYear} ({bulkHistory.length})
                    </p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {bulkHistory.map((row) => {
                        const emp = employees.find((e) => e.id === row.employee_id);
                        return (
                          <div key={row.employee_id} className="flex items-center gap-2 bg-white rounded px-2 py-1.5 text-xs" data-testid={`bulk-history-row-${row.employee_id}`}>
                            <span className="flex-1 font-bold truncate">{emp?.name || row.employee_id}</span>
                            <span className="font-numbers font-bold text-emerald-700">
                              {Number(row.weekly_target || 0).toLocaleString('en-US')}
                            </span>
                            <button
                              type="button"
                              onClick={() => deleteBulkHistoryRow(row.employee_id)}
                              className="text-destructive hover:opacity-70"
                              data-testid={`bulk-history-del-${row.employee_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {employees.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">لا توجد موظفين</p>
            ) : employees.map((emp) => (
              <Card key={emp.id} className="p-5 bg-accent/30 border-border">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold">{emp.name}</h4>
                    <p className="text-xs text-muted-foreground font-numbers">{emp.phone}</p>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
                    <div>
                      <Label className="text-xs font-bold">يومي</Label>
                      <Input type="number" step="0.01" value={emp.daily_target || 0}
                        onChange={(e) => handleEmployeeTargetChange(emp.id, 'daily_target', e.target.value)}
                        data-testid={`employee-daily-target-${emp.id}`}
                        className="text-right h-10 font-numbers" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold">أسبوعي</Label>
                      <Input type="number" step="0.01" value={emp.weekly_target || 0}
                        onChange={(e) => {
                          const v = e.target.value;
                          handleEmployeeTargetChange(emp.id, 'weekly_target', v);
                          const wt = Number(v);
                          if (Number.isFinite(wt) && wt >= 0) {
                            handleEmployeeTargetChange(emp.id, 'daily_target', String(+(wt / 7).toFixed(2)));
                          }
                        }}
                        data-testid={`employee-weekly-target-${emp.id}`}
                        className="text-right h-10 font-numbers" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold">شهري</Label>
                      <Input type="number" step="0.01" value={emp.monthly_target || 0}
                        onChange={(e) => handleEmployeeTargetChange(emp.id, 'monthly_target', e.target.value)}
                        data-testid={`employee-monthly-target-${emp.id}`}
                        className="text-right h-10 font-numbers" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold">ربع سنوي</Label>
                      <Input type="number" step="0.01" value={emp.quarterly_target || 0}
                        onChange={(e) => handleEmployeeTargetChange(emp.id, 'quarterly_target', e.target.value)}
                        data-testid={`employee-quarterly-target-${emp.id}`}
                        className="text-right h-10 font-numbers" />
                    </div>
                  </div>
                  <Button
                    onClick={() => handleEmployeeTargetSave(emp)}
                    data-testid={`save-employee-target-${emp.id}`}
                    size="sm"
                    className="gap-2"
                  >
                    <Save className="h-4 w-4" /> حفظ
                  </Button>
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function ResetAchievementsCard({ employees, onReset }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('all');
  const [employeeId, setEmployeeId] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requiredConfirmation = 'حذف';
  const canSubmit = confirmText.trim() === requiredConfirmation && (scope === 'all' || !!employeeId);

  const reset = async () => {
    setSubmitting(true);
    try {
      const params = {};
      if (scope === 'one' && employeeId) params.employee_id = employeeId;
      const res = await axios.post(`${API_URL}/api/sales/reset`, null, { params });
      toast.success(`تم حذف ${res.data.deleted} عملية بيع وإعادة تعيين المحققات`);
      setOpen(false);
      setConfirmText('');
      setScope('all');
      setEmployeeId('');
      onReset && onReset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إعادة التعيين');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-5 border-2 border-destructive/40 bg-destructive/5" data-testid="reset-achievements-card">
      <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="h-10 w-10 rounded-lg bg-destructive/15 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-destructive">منطقة خطرة — إعادة تعيين المحققات</h3>
            <p className="text-sm text-foreground/70 mt-1">
              هذه العملية تحذف مبيعات الموظفين السابقة وتُصفّر المحققات. الأهداف لن تتأثر.
            </p>
          </div>
        </div>

        <Dialog open={open} onOpenChange={(v) => {
          setOpen(v);
          if (!v) { setConfirmText(''); setScope('all'); setEmployeeId(''); }
        }}>
          <DialogTrigger asChild>
            <Button variant="destructive" data-testid="open-reset-dialog-button" className="gap-2 shrink-0">
              <RotateCcw className="h-4 w-4" />
              إعادة تعيين المحققات
            </Button>
          </DialogTrigger>
          <DialogContent dir="rtl" className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                تأكيد إعادة التعيين
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm">
                ⚠️ سيتم <b>حذف المبيعات نهائياً</b> ولا يمكن التراجع. الأهداف الموضوعة لن تتأثر.
              </div>

              <div className="space-y-2">
                <Label>نطاق الحذف</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger data-testid="reset-scope-select" className="text-right"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الموظفين في الفرع</SelectItem>
                    <SelectItem value="one">موظف محدد</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {scope === 'one' && (
                <div className="space-y-2">
                  <Label>اختر الموظف</Label>
                  <Select value={employeeId} onValueChange={setEmployeeId}>
                    <SelectTrigger data-testid="reset-employee-select" className="text-right"><SelectValue placeholder="اختر موظف" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>للتأكيد اكتب كلمة <b>حذف</b></Label>
                <Input
                  data-testid="reset-confirm-input"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="اكتب: حذف"
                  className="text-right"
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>إلغاء</Button>
                <Button
                  variant="destructive"
                  onClick={reset}
                  disabled={!canSubmit || submitting}
                  data-testid="confirm-reset-button"
                  className="gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  {submitting ? 'جاري الحذف...' : 'تأكيد وحذف'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
}

// Sunday-based week helpers (match backend _saudi_week_number_for)
const sunWeekNumberJS = (d) => {
  const jan1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const offset = (jan1.getUTCDay() + 7) % 7;
  const week1Start = new Date(jan1);
  week1Start.setUTCDate(jan1.getUTCDate() - offset);
  const delta = Math.floor((d - week1Start) / 86400000);
  return { year: d.getUTCFullYear(), week: Math.floor(delta / 7) + 1 };
};
const sunWeekRangeJS = (year, week) => {
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const offset = (jan1.getUTCDay() + 7) % 7;
  const week1Start = new Date(jan1);
  week1Start.setUTCDate(jan1.getUTCDate() - offset);
  const start = new Date(week1Start);
  start.setUTCDate(week1Start.getUTCDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const iso = (d) => d.toISOString().split('T')[0];
  return { start: iso(start), end: iso(end) };
};

function WeeklyTargetWeekPicker({ branchId, currentValue, onApplyValue }) {
  const _now = sunWeekNumberJS(new Date());
  const [year, setYear] = useState(_now.year);
  const [week, setWeek] = useState(_now.week);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!branchId) return;
    try {
      const r = await axios.get(`${API_URL}/api/branches/${branchId}/weekly-targets`);
      setItems(r.data.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open, branchId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Find any previously-saved target for the currently selected (year, week)
  const savedForCurrent = items.find((it) => it.year === year && it.week === week);

  // When a snapshot exists for the selected week, lift its value to the parent
  // input so the manager sees the ACTUAL saved amount (instead of the stale
  // branch default). This runs on open + whenever (year, week, items) changes.
  useEffect(() => {
    if (!open || !onApplyValue) return;
    if (savedForCurrent && savedForCurrent.weekly_target != null) {
      const wt = Number(savedForCurrent.weekly_target);
      const cur = Number(currentValue);
      if (Number.isFinite(wt) && wt !== cur) onApplyValue(wt);
    }
  }, [open, savedForCurrent?.weekly_target, year, week]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const wt = Number(currentValue);
    if (!Number.isFinite(wt) || wt < 0) { toast.error('قيمة المستهدف غير صحيحة'); return; }
    if (!branchId) { toast.error('فرع غير محدد'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/branches/${branchId}/weekly-targets/${year}/${week}`, { weekly_target: wt });
      toast.success(`تم حفظ مستهدف أسبوع ${week} - ${year}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحفظ');
    } finally { setSaving(false); }
  };

  const remove = async (yr, wk) => {
    if (!window.confirm(`حذف مستهدف أسبوع ${wk} - ${yr}؟`)) return;
    try {
      await axios.delete(`${API_URL}/api/branches/${branchId}/weekly-targets/${yr}/${wk}`);
      toast.success('تم الحذف');
      load();
    } catch (e) { toast.error('فشل الحذف'); }
  };

  const range = sunWeekRangeJS(year, week);
  const isCurrentWeek = year === _now.year && week === _now.week;
  return (
    <div className="mt-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-2.5 space-y-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-[11px] font-bold text-primary hover:text-primary/80"
        data-testid="weekly-picker-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" />
          حفظ مستهدف لأسبوع محدد (ماضٍ / مستقبلي)
        </span>
        <span className="text-[10px] opacity-60">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="space-y-2 pt-1">
          <div className="grid grid-cols-2 gap-1.5">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 text-[11px]" data-testid="weekly-picker-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 6 }, (_, i) => _now.year - 3 + i).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(week)} onValueChange={(v) => setWeek(Number(v))}>
              <SelectTrigger className="h-8 text-[11px]" data-testid="weekly-picker-week"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {Array.from({ length: 53 }, (_, i) => i + 1).map((w) => (
                  <SelectItem key={w} value={String(w)}>أسبوع {w}{w === _now.week && year === _now.year ? ' (الحالي)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-[10px] text-foreground/55 font-numbers">{range.start} → {range.end} {isCurrentWeek && '— الأسبوع الحالي'}</div>
          {/* Saved-value hint for the selected week */}
          {savedForCurrent ? (
            <div className="flex items-center justify-between gap-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
              <span className="font-bold text-emerald-800">
                مستهدف محفوظ لأسبوع {week}: <span className="font-numbers">{Number(savedForCurrent.weekly_target || 0).toLocaleString('en-US')}</span> ﷼
              </span>
              {onApplyValue && Number(currentValue) !== Number(savedForCurrent.weekly_target) && (
                <button
                  type="button"
                  onClick={() => onApplyValue(Number(savedForCurrent.weekly_target))}
                  className="text-[10px] font-bold text-emerald-700 hover:underline whitespace-nowrap"
                  data-testid="weekly-picker-apply-saved"
                >
                  تحميل في الإدخال
                </button>
              )}
            </div>
          ) : (
            <div className="text-[10px] text-foreground/55 bg-background border border-border rounded-md px-2 py-1.5">
              لا يوجد مستهدف محفوظ لأسبوع {week} — سيتم استخدام المبلغ في مربع الإدخال أعلاه.
            </div>
          )}
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={saving || !currentValue}
            className="w-full h-8 text-[11px] gap-1"
            data-testid="weekly-picker-save"
          >
            <Plus className="h-3.5 w-3.5" />
            {saving ? 'جاري الحفظ...' : `حفظ مبلغ ${currentValue || '—'} لأسبوع ${week}`}
          </Button>
          {/* List */}
          {items.length > 0 && (
            <div className="pt-1 border-t border-border/40 space-y-1 max-h-44 overflow-y-auto">
              <p className="text-[10px] font-bold text-foreground/70">السجل ({items.length})</p>
              {items.map((it) => {
                const r = sunWeekRangeJS(it.year, it.week);
                return (
                  <div key={`${it.year}-${it.week}`} className="flex items-center gap-1.5 bg-background rounded px-1.5 py-1 text-[10px]" data-testid={`weekly-picker-row-${it.year}-${it.week}`}>
                    <span className="px-1.5 py-0.5 rounded bg-primary text-white font-bold whitespace-nowrap">أ{it.week}/{it.year}</span>
                    <span className="text-foreground/55 font-numbers shrink-0">{r.start}→{r.end}</span>
                    <span className="flex-1 text-left font-numbers font-bold text-emerald-700">{Number(it.weekly_target || 0).toLocaleString('en-US')}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setYear(it.year);
                        setWeek(it.week);
                        if (onApplyValue) onApplyValue(Number(it.weekly_target || 0));
                      }}
                      className="text-primary hover:underline"
                      data-testid={`weekly-picker-load-${it.year}-${it.week}`}
                      title="تعديل هذا الأسبوع — يحمّل المبلغ في مربع الإدخال"
                    >
                      <Edit className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => remove(it.year, it.week)} className="text-destructive hover:underline" data-testid={`weekly-picker-del-${it.year}-${it.week}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
